"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";
import { feeLabel } from "@lib/fees";

/* ─── Types ─────────────────────────────────────────────────────────────── */
type Path = "discovery" | "farleigh" | "bodhi" | "consultation";

interface Option {
  label: string;
  scores: Partial<Record<Path, number>>;
}

interface Question {
  id: number;
  text: string;
  sub?: string;
  options: Option[];
}

/* ─── Quiz Data ──────────────────────────────────────────────────────────── */
const QUESTIONS: Question[] = [
  {
    id: 1,
    text: "What's your biggest health focus right now?",
    sub: "Pick the one that resonates most.",
    options: [
      { label: "Energy, sleep or mood", scores: { farleigh: 1, bodhi: 1 } },
      { label: "Weight, digestion or hormones", scores: { consultation: 1, farleigh: 1 } },
      { label: "Chronic symptoms or a long-standing condition", scores: { consultation: 2 } },
      { label: "Just curious — I want to feel better overall", scores: { discovery: 2 } },
    ],
  },
  {
    id: 2,
    text: "How long have you been working on this?",
    options: [
      { label: "I'm just starting out", scores: { discovery: 2 } },
      { label: "A few months — still finding my feet", scores: { bodhi: 1, farleigh: 1 } },
      { label: "Over a year — I've tried things but something's missing", scores: { farleigh: 2, consultation: 1 } },
      { label: "A long time — I need consistent, ongoing support", scores: { bodhi: 2, consultation: 1 } },
    ],
  },
  {
    id: 3,
    text: "What kind of support sounds most like you?",
    sub: "Be honest — there's no wrong answer.",
    options: [
      { label: "A personal deep-dive with a specialist, just for me", scores: { consultation: 2 } },
      { label: "A clear, step-by-step framework I can follow", scores: { farleigh: 2 } },
      { label: "An expert-led community I can be part of each month", scores: { bodhi: 2 } },
      { label: "A quick chat to understand my options first", scores: { discovery: 2 } },
    ],
  },
  {
    id: 4,
    text: "How ready are you to invest in your health?",
    options: [
      { label: "Not sure yet — I'm still exploring", scores: { discovery: 2 } },
      { label: "Open to a manageable monthly commitment", scores: { bodhi: 2 } },
      { label: "Ready to make a meaningful one-off investment", scores: { consultation: 1, farleigh: 1 } },
      { label: "Whatever it takes to get real, lasting results", scores: { consultation: 2 } },
    ],
  },
  {
    id: 5,
    text: "Three months from now, what would success look like?",
    sub: "Pick the outcome that excites you most.",
    options: [
      { label: "Feeling noticeably better in my day-to-day life", scores: { bodhi: 1, farleigh: 1 } },
      { label: "Understanding the root cause with a clear plan", scores: { consultation: 1, farleigh: 1 } },
      { label: "Sustainable habits supported by a like-minded community", scores: { bodhi: 2 } },
      { label: "Simply knowing which step to take first", scores: { discovery: 2 } },
    ],
  },
];

/* ─── Result Content ─────────────────────────────────────────────────────── */
const RESULTS: Record<
  Path,
  {
    emoji: string;
    title: string;
    tagline: string;
    description: string;
    cta: string;
    href: string;
    secondaryCta?: string;
    secondaryHref?: string;
    highlights: string[];
  }
> = {
  discovery: {
    emoji: "☕",
    title: "Start with a Free Discovery Call",
    tagline: "The perfect first step — no pressure, just clarity.",
    description:
      "You're in exploration mode, and that's exactly right. A free 15-minute call with Niki gives you a calm, open conversation to understand what you're dealing with — and a clear sense of the best next step, with zero obligation.",
    cta: "Book My Free Discovery Call",
    href: "/workwithme/discoverycall",
    highlights: [
      "100% free, no strings attached",
      "15 minutes — friendly & relaxed",
      "Leave with clarity on your best next step",
    ],
  },
  farleigh: {
    emoji: "🧭",
    title: "You're Ready for the Farleigh Method",
    tagline: "A structured, proven approach built around how your body actually responds.",
    description:
      "You've tried things before, but something hasn't clicked. The Farleigh Method strips away the guesswork — a clear, step-by-step protocol that tracks what changes and adjusts as you go. Not another plan. A framework that works.",
    cta: "Explore the Farleigh Method",
    href: "/workwithme/farleighMethod",
    secondaryCta: "Or start with a free chat",
    secondaryHref: "/workwithme/discoverycall",
    highlights: [
      "Systematic — one change at a time",
      "Root-cause focused, not symptom-masking",
      "Clear milestones you can measure",
    ],
  },
  bodhi: {
    emoji: "🌿",
    title: "The Bodhi Health Club is Your Match",
    tagline: "Expert-led monthly wellness — community, accountability, and real results.",
    description:
      "You want consistent, expert support without a large one-off commitment. The Bodhi Health Club delivers exactly that: monthly guidance from Naturopathic Nutritionist Niki Charnock and Integrative Medicine doctor Dr. Zoue Lloyd Wright, in a warm and supportive community.",
    cta: "Join the Bodhi Health Club",
    href: "/workwithme/align",
    secondaryCta: "Not sure? Book a free chat first",
    secondaryHref: "/workwithme/discoverycall",
    highlights: [
      `From ${feeLabel("bodhiClub")}/month — flexible, cancel anytime`,
      "Expert-led by Niki & Dr. Zoue Lloyd Wright",
      "Optional 1-to-1 add-on for deeper support",
    ],
  },
  consultation: {
    emoji: "✨",
    title: "A Personalised Consultation is Your Next Step",
    tagline: "Deep, root-cause healing — tailored entirely to you.",
    description:
      "You're ready to get to the bottom of it. A full consultation with Niki gives you a complete picture of your health, a personalised nutrition and lifestyle plan, and a clear roadmap forward. This is where lasting change begins.",
    cta: "Book a Consultation",
    href: "/workwithme/consultation",
    secondaryCta: "Want a free chat first?",
    secondaryHref: "/workwithme/discoverycall",
    highlights: [
      "60-minute in-depth 1-to-1 session",
      "Full health history & personalised plan",
      `From ${feeLabel("consultation")} — children from ${feeLabel("childConsultation")}`,
    ],
  },
};

/* ─── Schedule config per path ──────────────────────────────────────────── */
const SCHEDULE_CONFIG: Record<Path, { duration: number; meetingType: string; title: string; subtitle: string; isFree: boolean }> = {
  discovery: {
    duration: 15,
    meetingType: "discovery",
    title: "Book Your Free Discovery Call",
    subtitle: "Pick a time that suits you. 15 minutes, free, no obligation.",
    isFree: true,
  },
  farleigh: {
    duration: 60,
    meetingType: "farleigh-method",
    title: "Book Your Farleigh Method Consultation",
    subtitle: "A full 60-minute session — the structured start to your wellness journey.",
    isFree: false,
  },
  bodhi: {
    duration: 15,
    meetingType: "bodhi-discovery",
    title: "Book a Free Call to Join Bodhi Club",
    subtitle: "A quick 15-minute call with Niki to get you started in the community.",
    isFree: true,
  },
  consultation: {
    duration: 60,
    // Must stay "full-consultation": /api/consultations/initiate resolves the fee by
    // matching this string, and a bare "consultation" matches no branch — the booking
    // 400s before it ever reaches SumUp. Every other entry point sends this same value.
    meetingType: "full-consultation",
    title: "Book Your Personalised Consultation",
    subtitle: "A deep 60-minute 1-to-1 with Niki — where real, lasting change begins.",
    isFree: false,
  },
};

const PATH_LABELS: Record<Path, string> = {
  discovery: "Free Discovery Call",
  farleigh: "Farleigh Method",
  bodhi: "Bodhi Health Club",
  consultation: "Consultation",
};

/* ─── Scoring ────────────────────────────────────────────────────────────── */
function computeResult(answers: number[][]): Path {
  const totals: Record<Path, number> = { discovery: 0, farleigh: 0, bodhi: 0, consultation: 0 };
  answers.forEach((selected, qIdx) => {
    selected.forEach((optIdx) => {
      const scores = QUESTIONS[qIdx].options[optIdx].scores;
      (Object.keys(scores) as Path[]).forEach((p) => {
        totals[p] += scores[p] ?? 0;
      });
    });
  });
  return (Object.keys(totals) as Path[]).reduce((best, p) =>
    totals[p as Path] > totals[best] ? (p as Path) : best
  ) as Path;
}

/* ─── Animation variants ─────────────────────────────────────────────────── */
const slideIn: Variants = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.35 } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.2 } },
};

const fadeUp: Variants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/* ─── Page ───────────────────────────────────────────────────────────────── */
export default function QuizPage() {
  const [step, setStep] = useState<"intro" | "quiz" | "result">("intro");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<number[][]>(Array(QUESTIONS.length).fill([]));
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<Path | null>(null);

  // Lead capture form state
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadState, setLeadState] = useState<"idle" | "submitting" | "done" | "error">("idle");

  // UTM params captured on mount — attached to every track event
  const utmRef = useRef<Record<string, string>>({});

  // Capture UTM params and fire a page_view on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    utmRef.current = utm;
    try {
      track(
        "page_view",
        { label: "quiz-landing", component: "WorkWithMeQuiz" },
        { event: "page_view", label: "quiz-landing", component: "WorkWithMeQuiz", source: utm.utm_source, meta: utm }
      );
    } catch {}
  }, []);

  // Abandonment tracking — fires when user leaves mid-quiz
  useEffect(() => {
    if (step !== "quiz") return;
    const onHide = () => {
      try {
        track(
          "quiz_abandon",
          { label: `Q${current + 1}`, component: "WorkWithMeQuiz" },
          { event: "quiz_abandon", label: `Abandoned at Q${current + 1}`, component: "WorkWithMeQuiz", meta: { question: current + 1, ...utmRef.current } }
        );
      } catch {}
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [step, current]);

  const progress = Math.round(((current + (step === "result" ? 1 : 0)) / QUESTIONS.length) * 100);

  function handleStart() {
    try {
      track(
        "quiz_start",
        { label: "start", component: "WorkWithMeQuiz" },
        { event: "quiz_start", label: "start", component: "WorkWithMeQuiz", source: utmRef.current.utm_source, meta: utmRef.current }
      );
    } catch {}
    setStep("quiz");
  }

  function handleSelect(optIdx: number) {
    setSelected(optIdx);
  }

  function handleNext() {
    if (selected === null) return;
    const newAnswers = answers.map((a, i) => (i === current ? [selected] : a));
    setAnswers(newAnswers);

    try {
      track(
        "quiz_answer",
        { label: `Q${current + 1}:A${selected}`, component: "WorkWithMeQuiz" },
        { event: "quiz_answer", label: `Q${current + 1}:A${selected}`, component: "WorkWithMeQuiz", meta: { question: current + 1, answer: selected, ...utmRef.current } }
      );
    } catch {}

    if (current < QUESTIONS.length - 1) {
      setCurrent(current + 1);
      setSelected(null);
    } else {
      const path = computeResult(newAnswers);
      setResult(path);
      setStep("result");
      try {
        track(
          "quiz_complete",
          { label: path, component: "WorkWithMeQuiz" },
          { event: "quiz_complete", label: path, component: "WorkWithMeQuiz", source: utmRef.current.utm_source, meta: { result: path, ...utmRef.current } }
        );
      } catch {}
    }
  }

  function handleBack() {
    if (current === 0) {
      setStep("intro");
      setCurrent(0);
      setSelected(null);
      return;
    }
    setCurrent(current - 1);
    setSelected(answers[current - 1][0] ?? null);
  }

  function handleRetake() {
    setStep("intro");
    setCurrent(0);
    setAnswers(Array(QUESTIONS.length).fill([]));
    setSelected(null);
    setResult(null);
    setLeadState("idle");
    setLeadName("");
    setLeadEmail("");
  }

  async function handleLeadSubmit(e: { preventDefault(): void }) {
    e.preventDefault();
    if (!leadEmail || !leadName || !result) return;
    setLeadState("submitting");
    try {
      const res = await fetch("/api/quiz-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: leadName,
          email: leadEmail,
          result,
          utmSource:   utmRef.current.utm_source,
          utmMedium:   utmRef.current.utm_medium,
          utmCampaign: utmRef.current.utm_campaign,
          utmContent:  utmRef.current.utm_content,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setLeadState("done");
        try {
          track(
            "quiz_lead",
            { label: leadEmail, component: "WorkWithMeQuiz" },
            { event: "quiz_lead", label: leadEmail, component: "WorkWithMeQuiz", source: utmRef.current.utm_source, meta: { result, already_subscribed: data.alreadySubscribed, ...utmRef.current } }
          );
        } catch {}
      } else {
        setLeadState("error");
      }
    } catch {
      setLeadState("error");
    }
  }

  return (
    <div className="relative bg-[var(--color-brand)] min-h-screen text-[var(--color-text-primary)]">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-4xl px-5 md:px-10 pt-10 md:pt-16 pb-6 text-center">
        {step !== "result" && (
          <>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-balance">
              {step === "intro"
                ? <>Not sure where to start?<br className="hidden sm:block" /> Let's find your perfect fit.</>
                : <>Your personalised<br className="hidden sm:block" /> wellness path</>}
            </h1>
            <p className="mt-3 md:mt-5 text-sm md:text-lg opacity-90 max-w-2xl mx-auto text-pretty">
              {step === "intro"
                ? "Answer 5 quick questions and we'll recommend the right programme or conversation for exactly where you are right now."
                : `Question ${current + 1} of ${QUESTIONS.length}`}
            </p>
          </>
        )}

        {step === "result" && result && (
          <motion.div {...fadeUp}>
            <p className="inline-block text-xs font-semibold tracking-widest uppercase text-[var(--color-text-brand-highlight)] mb-3">
              Your Result
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-balance">
              {RESULTS[result].title}
            </h1>
            <p className="mt-3 md:mt-5 text-sm md:text-lg opacity-90 max-w-2xl mx-auto">
              {RESULTS[result].tagline}
            </p>
          </motion.div>
        )}
      </header>

      {/* ── Body card ─────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-4xl -mt-2 md:-mt-4 bg-[var(--color-bg-muted)] rounded-t-3xl shadow-2xl px-5 md:px-10 pb-32 md:pb-20 min-h-[60vh]">

        {/* Progress bar — shown during quiz */}
        {step === "quiz" && (
          <div className="pt-6 pb-2">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span>{current + 1} / {QUESTIONS.length}</span>
              <span>{progress}% complete</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-[var(--color-brand)]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeOut" as const }}
              />
            </div>
          </div>
        )}

        {/* ── INTRO ─────────────────────────────────────────────────── */}
        {step === "intro" && (
          <motion.div {...fadeUp} className="pt-10 md:pt-14 text-center max-w-2xl mx-auto">
            {/* Trust signals */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-10">
              {[
                { icon: "⏱", label: "2 minutes", sub: "to complete" },
                { icon: "🎯", label: "Personalised", sub: "just for you" },
                { icon: "🔒", label: "No commitment", sub: "ever required" },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-[var(--border-accent)] p-3 md:p-4 shadow-sm">
                  <div className="text-2xl mb-1">{item.icon}</div>
                  <p className="font-semibold text-[var(--color-brand)] text-sm">{item.label}</p>
                  <p className="text-xs text-[var(--text-on-light-faint)]">{item.sub}</p>
                </div>
              ))}
            </div>

            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-8">
              We'll match you with one of four pathways — a <strong>free Discovery Call</strong>, the{" "}
              <strong>Farleigh Method</strong>, the <strong>Bodhi Health Club</strong>, or a{" "}
              <strong>personalised Consultation</strong> — based on where you are right now. Your result
              reveals at the end.
            </p>

            <button
              onClick={handleStart}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--color-brand)] text-[var(--btn-anchor-text)] font-bold text-base md:text-lg px-10 py-4 shadow-lg hover:bg-[var(--btn-anchor-hover)] transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Start the Quiz →
            </button>

            <p className="mt-4 text-xs text-gray-400">
              Used by people discovering CF Organic Wellness via Instagram, LinkedIn &amp; email
            </p>

            {/* Social proof */}
            <div className="mt-10 grid md:grid-cols-2 gap-4 text-left">
              {[
                {
                  quote: "I've never felt so well, so grounded, and so in tune with myself.",
                  body: "Nichola helped me detox and reclaim my health. I feel emotionally and spiritually stronger than ever.",
                  name: "Lucie W.",
                  role: "A Life Changing Journey to Wellness",
                },
                {
                  quote: "Niki's passion and enthusiasm is infectious.",
                  body: "Her patience in answering any questions is endless. Everything we learned was accessible and simple to continue at home.",
                  name: "Jayne G.",
                  role: "Retreat Guest, March 2025",
                },
              ].map((t) => (
                <div key={t.name} className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
                  <p className="text-sm font-semibold text-[var(--color-brand)] italic leading-snug mb-2">"{t.quote}"</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{t.body}</p>
                  <p className="mt-3 text-xs font-semibold text-gray-700">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.role}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ── QUIZ ──────────────────────────────────────────────────── */}
        {step === "quiz" && (
          <div className="pt-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                variants={slideIn}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-xl md:text-2xl font-bold text-[var(--color-text-brand)] mt-4 mb-1 text-balance">
                    {QUESTIONS[current].text}
                  </h2>
                  {QUESTIONS[current].sub && (
                    <p className="text-sm text-gray-500 mb-5">{QUESTIONS[current].sub}</p>
                  )}
                  {!QUESTIONS[current].sub && <div className="mb-5" />}

                  <div className="space-y-3">
                    {QUESTIONS[current].options.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSelect(idx)}
                        className={`w-full text-left rounded-2xl border-2 px-5 py-4 transition-all text-sm md:text-base font-medium leading-snug
                          ${selected === idx
                            ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white shadow-md scale-[1.01]"
                            : "border-gray-200 bg-white text-gray-700 hover:border-[var(--color-brand)] hover:bg-[var(--color-brand)]/5"
                          }`}
                      >
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-3 flex-shrink-0
                          ${selected === idx ? "bg-white text-[var(--color-brand)]" : "bg-gray-100 text-gray-500"}`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-8 gap-3">
                    <button
                      onClick={handleBack}
                      className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1 py-2 px-1 min-h-[44px]"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={selected === null}
                      className={`inline-flex items-center gap-2 rounded-full font-bold px-8 py-3 min-h-[44px] text-sm md:text-base transition-all
                        ${selected !== null
                          ? "bg-[var(--color-brand)] text-[var(--btn-anchor-text)] shadow-md hover:bg-[var(--btn-anchor-hover)] hover:scale-[1.02] active:scale-[0.98]"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                      {current < QUESTIONS.length - 1 ? "Next →" : "See My Result →"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* ── RESULT ────────────────────────────────────────────────── */}
        {step === "result" && result && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" as const }}
            className="pt-10 md:pt-12 max-w-2xl mx-auto"
          >
            {/* Emoji + result badge */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--color-brand)]/10 border-2 border-[var(--color-brand)] text-4xl mb-4"
              >
                {RESULTS[result].emoji}
              </motion.div>
              <div className="inline-block bg-[var(--color-brand)] text-white text-xs font-bold tracking-widest uppercase rounded-full px-4 py-1.5">
                Your Recommendation
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-700 text-sm md:text-base leading-relaxed text-center mb-8">
              {RESULTS[result].description}
            </p>

            {/* Highlights */}
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-8">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">What this includes</p>
              <ul className="space-y-2">
                {RESULTS[result].highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-[var(--color-brand)] font-bold mt-0.5">✓</span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Inline booking widget */}
            <div id="quiz-schedule" className="mt-10 scroll-mt-24 rounded-3xl bg-white border-2 border-[var(--color-brand)] shadow-lg overflow-hidden">
              <div className="bg-[var(--color-brand)] px-6 py-4 text-center">
                <p className="text-white font-extrabold text-lg md:text-xl">
                  {SCHEDULE_CONFIG[result].title}
                </p>
                <p className="text-white/80 text-sm mt-1">
                  {SCHEDULE_CONFIG[result].subtitle}
                </p>
              </div>
              <div className="px-4 md:px-6 pb-6">
                <ScheduleCall
                  duration={SCHEDULE_CONFIG[result].duration}
                  meetingType={SCHEDULE_CONFIG[result].meetingType}
                  isFree={SCHEDULE_CONFIG[result].isFree}
                  componentName="WorkWithMeQuiz"
                />
              </div>
            </div>

            {/* Lead capture — for people not ready to book */}
            {leadState !== "done" ? (
              <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-bold text-[var(--color-brand)] mb-0.5">
                  Not ready to book just yet?
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Drop your details and we'll send your personalised result to your inbox so you have it when the time is right.
                </p>
                <form onSubmit={handleLeadSubmit} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    required
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/30"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    required
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/30"
                  />
                  <button
                    type="submit"
                    disabled={leadState === "submitting"}
                    className="flex-shrink-0 rounded-xl bg-[var(--color-brand)] text-[var(--btn-anchor-text)] text-sm font-bold px-5 py-2.5 hover:bg-[var(--btn-anchor-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {leadState === "submitting" ? "Saving…" : "Save my result"}
                  </button>
                </form>
                {leadState === "error" && (
                  <p className="mt-2 text-xs text-red-500">Something went wrong — please try again.</p>
                )}
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-[var(--color-brand)]/20 bg-[var(--color-brand)]/5 p-5 text-center">
                <p className="text-sm font-bold text-[var(--color-brand)]">Result saved ✓</p>
                <p className="text-xs text-gray-500 mt-1">Check your inbox — we've sent your recommendation over.</p>
              </div>
            )}

            {/* Explore all options */}
            <div className="mt-14">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-5">
                Explore all options
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.keys(RESULTS) as Path[]).map((p) =>
                  p === result ? (
                    <div
                      key={p}
                      className="flex items-center gap-3 rounded-2xl bg-[var(--color-brand)] text-white px-5 py-4 shadow-md"
                    >
                      <span className="text-2xl flex-shrink-0">{RESULTS[p].emoji}</span>
                      <div className="min-w-0">
                        <p className="font-bold text-sm leading-tight">{PATH_LABELS[p]}</p>
                        <p className="text-white/70 text-xs mt-0.5">Your recommended path ✓</p>
                      </div>
                    </div>
                  ) : (
                    <TrackedLink
                      key={p}
                      href={RESULTS[p].href}
                      variant="outline"
                      size="sm"
                      color="neutral"
                      event="click"
                      label={`Quiz All Options - ${p}`}
                      component="WorkWithMeQuiz"
                      className="flex items-center gap-3 rounded-2xl px-5 py-4 text-left w-full"
                    >
                      <span className="text-2xl flex-shrink-0">{RESULTS[p].emoji}</span>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-gray-700 leading-tight">{PATH_LABELS[p]}</p>
                        <p className="text-gray-400 text-xs mt-0.5">Learn more →</p>
                      </div>
                    </TrackedLink>
                  )
                )}
              </div>
            </div>

            {/* Retake */}
            <div className="text-center mt-8 mb-4">
              <button
                onClick={handleRetake}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline underline-offset-2 py-2 min-h-[44px]"
              >
                Retake the quiz
              </button>
            </div>
          </motion.div>
        )}
      </main>

      {/* ── Sticky mobile CTA (intro only) ─────────────────────────── */}
      {step === "intro" && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
            <span className="text-sm font-medium text-gray-800">Find your perfect fit</span>
            <button
              onClick={handleStart}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--color-brand)] text-[var(--btn-anchor-text)] font-bold text-sm px-6 py-2.5 shadow hover:bg-[var(--btn-anchor-hover)] transition-all"
            >
              Take the Quiz →
            </button>
          </div>
        </div>
      )}

      {/* ── Sticky mobile CTA (result) ──────────────────────────────── */}
      {step === "result" && result && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Your match</p>
              <p className="text-sm font-bold text-gray-800 truncate">{RESULTS[result].emoji} {PATH_LABELS[result]}</p>
            </div>
            <button
              onClick={() => document.getElementById("quiz-schedule")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-[var(--color-brand)] text-[var(--btn-anchor-text)] font-bold text-sm px-5 py-2.5 shadow hover:bg-[var(--btn-anchor-hover)] transition-all"
            >
              Book Now ↓
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
